import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { signOut } from 'aws-amplify/auth';
import { useRouter, usePathname } from 'next/navigation';
import QuestionnaireModal from 'components/Contact/questionnaire';
import { SecurityUtils } from 'pp-web';
import { Box, AppBar, Toolbar, IconButton, Typography, Divider, ListItemIcon, Drawer, Button, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import React from 'react';
import SettingsDialog from '../Settings/SettingsDialog';
import * as PolnApi from '../../utils/api-service'

export default function UserDealNavbar(
    {
        logo,
        user,
        questions,
        questionaireModal,
        setQuestionaireModal,
        setLoading,
        reset,
        doSearch,
        inputValue,
        setInputValue,
        initialSearch = '',
        categories = [],
        selectedNav,
        setSelectedNav
    }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mobileOpenMenuDrawer, setMobileOpenMenuDrawer] = React.useState(false);
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const router = useRouter();
    const pathname = usePathname();

    React.useEffect(() => {
        setInputValue(initialSearch);
    }, [initialSearch]);

    const isMenuOpen = Boolean(anchorEl);
    const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const toggleDrawer = () => setMobileOpenMenuDrawer(!mobileOpenMenuDrawer);

    const logout = async () => {
        setLoading(true);
        await signOut();
        router.push('/landing/deals');
        setLoading(false);
    };

    // --- Custom pointer for menu ---
    const menuPointer = {
        position: 'absolute',
        top: -8,
        right: 32,
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #F7F7FB',
        zIndex: 1,
    };

    // --- Nav Links ---
    const navLinks = [
        { label: 'Dashboard', path: '/deals/creator/revenue' },
        { label: 'Deal Lists', path: '/deals/creator' },
    ];

    // --- Render Menu ---
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
            PaperProps={{
                sx: {
                    mt: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                    width: 320,
                    maxWidth: '90vw',
                    bgcolor: '#F7F7FB',
                    p: 0,
                    overflow: 'visible',
                    position: 'relative',
                },
            }}
            MenuListProps={{ sx: { p: 0 } }}
        >
            <Box sx={menuPointer} />
            <Box sx={{ p: 2, borderBottom: '1px solid #E0E0E0' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#181A2A' }}>
                    {user?.name || 'User Name'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>
                    {user?.email || 'user@email.com'}
                </Typography>
            </Box>
            <MenuItem onClick={() => {
                handleMenuClose();
                router.push('/page/datadyno-support/');
            }}>
                <ListItemIcon><HeadphonesIcon fontSize="small" /></ListItemIcon>
                <Typography>Support</Typography>
            </MenuItem>
            {!pathname.includes('/deals/user') && (
                <MenuItem onClick={() => {
                    handleMenuClose();
                    setSettingsOpen(true);
                }}>
                    <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                    <Typography>Settings</Typography>
                </MenuItem>
            )}
            <MenuItem onClick={logout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <Typography>Logout</Typography>
            </MenuItem>
        </Menu>
    );

    // --- Mobile Drawer ---
    const renderDrawer = (
        <Drawer
            anchor="right"
            open={mobileOpenMenuDrawer}
            onClose={toggleDrawer}
            sx={{
                '& .MuiDrawer-paper': {
                    width: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#F7F7FB',
                    p: 0,
                },
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pt: 2, pr: 2, pb: 1 }}>
                <IconButton onClick={toggleDrawer} sx={{ p: 0.5 }}>
                    <CloseIcon sx={{ width: 32, height: 32, fontWeight: 'bold', color: 'red' }} />
                </IconButton>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {(user && SecurityUtils.isInternalUser(user)) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, px: 2, pb: 2, pt: 1 }}>
                    {navLinks.map((link) => (
                        <Button
                            key={link.path}
                            onClick={() => { router.push(link.path); toggleDrawer(); }}
                            sx={{
                                color: '#181A2A',
                                fontWeight: 700,
                                width: '100%',
                                fontSize: '1.1rem',
                                letterSpacing: 0.5,
                                py: 1.5,
                                borderRadius: 2,
                                background: 'transparent',
                                transition: 'background 0.2s',
                                '&:hover': {
                                    background: '#E7EAFE',
                                },
                            }}
                        >
                            {link.label}
                        </Button>
                    ))}
                </Box>
            )}
        </Drawer>
    );

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (inputValue.trim()) {
                doSearch(inputValue);
            }
        }
    };

    const handleReset = (event) => {
        setInputValue('');
        reset();
    };

    const handleSearch = () => {
        if (inputValue.trim()) {
            doSearch(inputValue.trim());
        } else {
            doSearch(null);
        }
    };

    const handleSettingsSave = async (data, setSaveLoading) => {
        // This will be implemented by you for the backend
        setSaveLoading(true)
        let res = await PolnApi.request('/creator/settings', {
            method: 'POST',
            body: data
        })
        setSaveLoading(false);
    };

    // --- Main Render ---
    return (
        <Box sx={{ flexGrow: 1, bgcolor: '#ffffff' }}>
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#EBEAF3', boxShadow: '0 2px 8px 0 #E6E6F0', p: 0 }}>
                <Toolbar sx={{ minHeight: 80, px: { xs: 1, sm: 3, md: 6 } }}>
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, ml: 1 }}>
                        <img src={logo?.url} alt={logo?.alt || 'Logo'} style={{ height: 36, width: 'auto' }} />
                    </Box>
                    {/* Nav Links (center) */}
                    {(!isMobile && user && SecurityUtils.isInternalUser(user) && !pathname.includes('/deals/user')) && (
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                            {navLinks.map((link, idx) => (
                                <React.Fragment key={link.path}>
                                    {idx > 0 && (
                                        <Box sx={{ height: 28, width: '2px', background: '#181A2A', opacity: 0.15, mx: 2, borderRadius: 1 }} />
                                    )}
                                    <Button
                                        onClick={() => router.push(link.path)}
                                        sx={{
                                            color: '#181A2A',
                                            fontWeight: 700,
                                            fontSize: '1.2rem',
                                            textTransform: 'none',
                                            borderBottom: pathname === link.path ? '2px solid #5B5BF6' : '2px solid transparent',
                                            borderRadius: 0,
                                        }}
                                    >
                                        {link.label}
                                    </Button>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                    {/* Actions (right) */}
                    {user && <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: { xs: 0, sm: 2 }, justifyContent: 'flex-end' }}>
                        <IconButton
                            edge="end"
                            aria-label="account of current user"
                            aria-controls="profile-menu"
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            sx={{
                                bgcolor: '#fff',
                                border: '2px solid #BFC8E6',
                                width: 48,
                                height: 48,
                                mr: { xs: 1.5, sm: 2 }
                            }}
                        >
                            <AccountCircle sx={{ width: 36, height: 36, color: '#181A2A' }} />
                        </IconButton>
                        {isMobile && user && SecurityUtils.isInternalUser(user) && !pathname.includes('/deals/user') && (
                            <IconButton color="inherit" onClick={toggleDrawer} sx={{ ml: 1 }}>
                                <MenuIcon sx={{ width: 32, height: 32, color: '#181A2A' }} />
                            </IconButton>
                        )}
                    </Box>
                    }
                </Toolbar>
            </AppBar>
            {user && renderMenu}
            {renderDrawer}
            <SettingsDialog
                open={settingsOpen}
                onClose={(e, callback) => {
                    setSettingsOpen(false);
                    callback && callback();
                }}
                onSave={handleSettingsSave}
                user={user}
            />
            {/* Welcome Message */}
            {pathname !== '/deals/creator/revenue' && (
                <Box sx={{ mt: 6, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {user && <Typography
                        variant="h3"
                        sx={{ fontWeight: 700, color: '#181A2A', textAlign: 'center', mb: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}
                    >
                        Welcome Back,{' '}
                        <Box component="span" sx={{ color: 'var(--theme-dark, #2B4D59)', fontWeight: 700 }}>{user?.name || 'User'}</Box>
                    </Typography>
                    }
                </Box>
            )}
            {/* Search Container */}
            {pathname !== '/deals/creator/revenue' && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box
                        sx={{
                            width: { xs: '98%', sm: '90%', md: '80%', lg: '80%' },
                            maxWidth: '80%',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: '1px solid #E0E0E0',
                            borderRadius: '50px',
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                            pl: 3,
                            py: 1,
                            pr: { xs: 1, md: 3 }
                        }}
                    >
                        <InputBase
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Ask anything to find the products you want, e.g. bluetooth speakers with over 5k reviews and 4+ stars..."
                            inputProps={{
                                'aria-label': 'search',
                                style: {
                                    fontSize: '1rem',
                                }
                            }}
                            sx={{
                                flex: 1,
                                color: '#1a1a1a',
                                '& .MuiInputBase-input': {
                                    '&::placeholder': {
                                        color: '#666',
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                        {inputValue && (
                            <IconButton
                                onClick={handleReset}
                                aria-label="Clear search"
                                size="small"
                                sx={{
                                    mx: 1,
                                    color: '#666'
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                        {isMobile ? (
                            <IconButton
                                onClick={handleSearch}
                                aria-label="Search"
                            >
                                <SearchIcon />
                            </IconButton>
                        ) : (
                            <Button
                                onClick={handleSearch}
                                variant="contained"
                                sx={{
                                    bgcolor: 'var(--theme-dark, #2B4D59)',
                                    color: 'white',
                                    borderRadius: '50px',
                                    border: '1px solid',
                                    borderColor: 'var(--theme-color, #436B78)',
                                    px: 4,
                                    py: 1,
                                    minWidth: '120px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    '&:hover': {
                                        bgcolor: 'var(--theme-dark, #2B4D59)',
                                        opacity: 0.9,
                                    },
                                }}
                            >
                                Search
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
            {questionaireModal && (
                <QuestionnaireModal
                    questionaireModal={questionaireModal}
                    setQuestionaireModal={setQuestionaireModal}
                    questions={questions}
                    email={user?.email}
                    edit
                />
            )}
        </Box>
    );
}
